import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ASSIGNMENT_STATUSES, EditTask, TASK_STATUSES, TVerseStatusesValues, WorkspaceAssignment, WorkspaceAssignmentsPerProject, WorkspaceTask } from '@/types/assignments';
import { Status, STATUSES } from '@/types/store';
import { Assignment } from '@/types/projects';
import { MUTATION_UPDATE_ASSIGNMENT, QUERY_TASKS_PER_ASSIGNMENT } from '@/graphql/queries/assignments';
import { BOOKMARK_TASK_MUTATION, MUTATION_UPDATE_TASK_PEER_REVIEW, MUTATION_UPDATE_TASK_TRANSLATE } from '@/graphql/queries/tasks';
import { RootState } from '..';
import { createAppAsyncThunk } from '../withTypes';
import { graphqlMutation, graphqlRequest, addRejectedToast, UNAUTHORIZED_ACTION_MESSAGE } from '../../api/graphqlClient';
import { groupBy, showToast } from '@/utils/utils';
import { errorMessages } from '@/utils/errorMessages';
import { successMessages } from '@/utils/successMessages';
import { TasksPerAssignmentQuery, TasksPerAssignmentQueryVariables } from '@/graphql/operation-types';

interface AssignmentsState {
  assignments: WorkspaceAssignmentsPerProject;
  status: Status;
  error: string;
}

const initialState: AssignmentsState = {
  assignments: {},
  status: STATUSES.IDLE,
  error: '',
};

interface UpdateTaskPayloadPartial {
  taskId: number;
  projectId: number;
  assignmentId: number;
  bookId: number;
  chapterId: number;
  verseId: number;
  status?: TVerseStatusesValues;
  hasError?: boolean;
  editReviewerTranslation?: boolean;
}

type CompleteTaskPayload = UpdateTaskPayloadPartial & EditTask;

interface BookmarkTaskPayload extends UpdateTaskPayloadPartial {
  isBookmarked: boolean;
}
interface MarkIncompleteTasksWithErrorPayload {
  projectId: number;
  assignmentId: number;
}

interface SaveAssignmentTempPayload {
  projectId: number;
  assignmentId: number;
  targetCompletionDate?: Assignment['targetCompletionDate'];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapBeTaskTranslation: (taskTranslation: any, isReviewAssignment: boolean) => WorkspaceTask = ({ // TODO: Remove any
  id,
  status_id,
  is_bookmarked,
  data: {
    book_id,
    book_title,
    chapter_id,
    chapter_title,
    source_sentence_id,
    source_sentence_number,
    source_sentence_text,
    translated_sentence_text,
    source_sentence,
    translated_sentence,
  },
  notes,
}, isReviewAssignment) => isReviewAssignment
  ? ({ // TODO: Generalize data, peer review structure is better, translatetaskdata could be updated to have the same structure.
    id,
    bookId: book_id || source_sentence.book_id,
    bookName: book_title || source_sentence.book_title,
    chapterId: chapter_id || source_sentence.chapter_id,
    chapterNumber: chapter_title || source_sentence.chapter_title,
    verseId: source_sentence_id || source_sentence.id,
    verseNumber: source_sentence_number ?? source_sentence.sentence_number,
    verseText: source_sentence_text || source_sentence.sentence_text,
    status: status_id,
    isBookmarked: is_bookmarked,
    translation: translated_sentence?.sentence_text,
    note: notes[0]?.text || '',
    isReviewerView: true,
    editReviewerTranslation: false,
    hasError: false,
    translatedSentenceId: translated_sentence?.id,
    isReviewCompleted: status_id === TASK_STATUSES.COMPLETED,
  })
  : ({
    id,
    bookId: book_id || source_sentence.book_id,
    bookName: book_title || source_sentence.book_title,
    chapterId: chapter_id || source_sentence.chapter_id,
    chapterNumber: chapter_title || source_sentence.chapter_title,
    verseId: source_sentence_id || source_sentence.id,
    verseNumber: source_sentence_number ?? source_sentence.sentence_number,
    verseText: source_sentence_text || source_sentence.sentence_text,
    status: status_id,
    translation: translated_sentence_text || translated_sentence?.sentence_text,
    isBookmarked: is_bookmarked,
    note: notes[0]?.text || '',
    isReviewerView: false,
  });

function tasksByBookGroupByCallback(tasks: TasksPerAssignmentQuery['tasksPerAssignment'][0]) {
  const data = tasks.data;

  const bookId = data && 'book_id' in data && data?.book_id;
  const sourceSentenceBookId = data && 'source_sentence' in data && data.source_sentence?.book_id;

  return bookId || sourceSentenceBookId || -1;
}

function tasksByChapterGroupByCallback(tasks: TasksPerAssignmentQuery['tasksPerAssignment'][0]) {
  const data = tasks.data;

  const chapterId = data && 'chapter_id' in data && data?.chapter_id;
  const sourceSentenceChapterId = data && 'source_sentence' in data && data.source_sentence?.chapter_id;

  return chapterId || sourceSentenceChapterId || -1;
}

/* THUNKS */
export const fetchAssignment = createAppAsyncThunk('assignments/fetchAssignment', async ({ assignmentId, isReviewAssignment }: { projectId: number, assignmentId: number, isReviewAssignment: boolean }) => {
  const response = await graphqlRequest<
    TasksPerAssignmentQueryVariables,
    TasksPerAssignmentQuery
  >(
    QUERY_TASKS_PER_ASSIGNMENT,
    {
      assignmentId,
    },
  );

  const tasks = response.tasksPerAssignment;
  const tasksByBook = groupBy(tasks, tasksByBookGroupByCallback);

  const books: WorkspaceAssignment['books'] = Object.assign({}, ...Object.entries(tasksByBook).map(([bookId, bookTasks]) => {
    const tasksByChapter = groupBy(bookTasks || [], tasksByChapterGroupByCallback);

    return {
      [bookId]: {
        id: bookId,
        chapters: Object.assign({}, ...Object.entries(tasksByChapter).map(([chapterId, chapterTasks]) => {
          return {
            [chapterId]: {
              id: chapterId,
              verses: Object.assign({}, ...(chapterTasks || []).map((sentenceTask) => {
                const data = sentenceTask.data;
                const source_sentence_id = data && 'source_sentence_id' in data ? data.source_sentence_id : undefined;
                const source_sentence = data && 'source_sentence' in data ? data.source_sentence : undefined;
                const sentenceId = source_sentence_id || source_sentence?.id || -1;
                return {
                  [+sentenceId]: mapBeTaskTranslation(sentenceTask, isReviewAssignment),
                };
              })),
            },
          };
        })),
      },
    };
  }));

  return {
    id: assignmentId,
    books,
  };
});

export const bookmarkTaskThunk = createAppAsyncThunk('assignments/bookmarkTask', async ({ taskId, isBookmarked }: BookmarkTaskPayload) => {
  // TODO: Later it could be easier to map the response and update the whole task,
  // instead of modifying the state when the API call is successful
  await graphqlMutation(
    BOOKMARK_TASK_MUTATION,
    {
      taskId,
      isBookmarked,
    },
  );
});

export const completeTaskTranslateThunk = createAppAsyncThunk('assignments/completeTask', async ({ taskId, translation, note }: CompleteTaskPayload) => {
  // TODO: Later it could be easier to map the response and update the whole task,
  // instead of modifying the state when the API call is successful

  // TODO: Use same thunk for peer review ???

  await graphqlMutation(
    MUTATION_UPDATE_TASK_TRANSLATE,
    {
      id: taskId,
      sentenceText: translation,
      note,
    },
  );
});

export const completeTaskPeerReviewThunk = createAppAsyncThunk('assignments/completeTask', async ({ taskId, sentenceText, translatedSentenceId }: CompleteTaskPayload) => {
  try {
    // TODO: Later it could be easier to map the response and update the whole task,
    // instead of modifying the state when the API call is successful
    await graphqlMutation(
      MUTATION_UPDATE_TASK_PEER_REVIEW,
      {
        taskId,
        sentenceText,
        translatedSentenceId,
      },
    );
  } catch (error) {
    // Re-throw so the thunk rejects instead of resolving fulfilled; the
    // shared `.rejected` handler for `assignments/completeTask` (registered
    // via addRejectedToast on completeTaskTranslateThunk — same action type)
    // owns the user-facing toast and already suppresses on UNAUTHORIZED.
    // Without this re-throw, callers like handleReviewerComplete that
    // advance UI state would skip past an unsaved review on a per-action
    // FORBIDDEN.
    throw error;
  }
});
export const completeAssignmentThunk = createAppAsyncThunk('assignments/completeAssignment', async ({ assignmentId }: { assignmentId: number, projectId: number }) => {
  try {
    const result = await graphqlMutation(
      MUTATION_UPDATE_ASSIGNMENT,
      {
        id: assignmentId,
        assignment: {
          status_id: ASSIGNMENT_STATUSES.COMPLETED,
        },
      },
    );
    showToast(successMessages.assignmentSubmitted, 'success');
    return result; // Return the result so the thunk properly resolves
  } catch (error) {
    if ((error as { message?: string })?.message !== UNAUTHORIZED_ACTION_MESSAGE) {
      showToast(errorMessages.submittingAssignment, 'error');
    }
    throw error; // Re-throw the error so the caller can handle it
  }
});
/* END THUNKS */

const getAssignmentProjectState = (state: AssignmentsState, projectId: number) => {
  if (!state.assignments[projectId]) {
    state.assignments[projectId] = {
      id: projectId,
      assignments: {},
      assignmentsTemp: {},
    };
  }

  return state.assignments[projectId];
};

const assignmentsSlice = createSlice({
  name: 'assignments',
  initialState: initialState,
  reducers: {
    markIncompleteTasksWithError: (state, action: PayloadAction<MarkIncompleteTasksWithErrorPayload>) => {
      const { projectId, assignmentId } = action.payload;
      const assignment = state.assignments[projectId]?.assignments[assignmentId];

      if (assignment?.books) {
        Object.values(assignment.books).forEach((book) => {
          Object.values(book.chapters).forEach((chapter) => {
            Object.values(chapter.verses).forEach((task) => {
              if (task.status !== TASK_STATUSES.COMPLETED) {
                task.hasError = true;
              }
            });
          });
        });
      }
    },
    updateEditReviewTask: (state, action: PayloadAction<UpdateTaskPayloadPartial>) => {
      const { projectId, assignmentId, bookId, chapterId, verseId, editReviewerTranslation } = action.payload;

      const task = state.assignments[projectId]?.assignments[assignmentId]?.books[bookId]?.chapters[chapterId]?.verses[verseId];

      if (task) {
        task.editReviewerTranslation = editReviewerTranslation;
      }
    },
    updateTaskError: (state, action: PayloadAction<UpdateTaskPayloadPartial>) => {
      const { projectId, assignmentId, bookId, chapterId, verseId, hasError } = action.payload;

      const task = state.assignments[projectId]?.assignments[assignmentId]?.books[bookId]?.chapters[chapterId]?.verses[verseId];

      if (task) {
        task.hasError = hasError;
      }
    },
    saveAssignmentTemp: (state, action: PayloadAction<SaveAssignmentTempPayload>) => {
      const { projectId, assignmentId, ...assignmentTempPayload } = action.payload;
      const project = getAssignmentProjectState(state, projectId);

      project.assignmentsTemp[assignmentId] = {
        ...project.assignmentsTemp[assignmentId],
        ...assignmentTempPayload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAssignment.pending, (state) => {
      state.status = STATUSES.PENDING;
    });
    builder.addCase(fetchAssignment.fulfilled, (state, action) => {
      const { projectId, assignmentId } = action.meta.arg;
      const project = getAssignmentProjectState(state, projectId);
      // Always update with fresh data
      project.assignments[assignmentId] = action.payload;
      state.status = STATUSES.FULFILLED;
    });
    addRejectedToast(builder, fetchAssignment, errorMessages.fetchingAssignment);
    builder.addCase(bookmarkTaskThunk.fulfilled, (state, action) => {
      const { projectId, assignmentId, bookId, chapterId, verseId, isBookmarked } = action.meta.arg;
      const task = state.assignments[projectId]?.assignments[assignmentId]?.books[bookId]?.chapters[chapterId]?.verses[verseId];

      if (task) {
        task.isBookmarked = isBookmarked;
      }
    });
    addRejectedToast(builder, bookmarkTaskThunk, errorMessages.bookmarkingTask);
    builder.addCase(completeTaskTranslateThunk.fulfilled, (state, action) => {
      const {
        projectId, assignmentId, bookId, chapterId, verseId, translation, note,
        sentenceText, isReviewCompleted, /* , hasError, editReviewerTranslation */
      } = action.meta.arg;

      const task = state.assignments[projectId]?.assignments[assignmentId]?.books[bookId]?.chapters[chapterId]?.verses[verseId];
      if (task) {
        task.translation = translation || '';
        task.note = note || '';
        task.status = TASK_STATUSES.COMPLETED;
        task.isReviewCompleted = isReviewCompleted || false;
        task.hasError = false;
        task.sentenceText = sentenceText;
        task.editReviewerTranslation = false;
      }
    });
    addRejectedToast(builder, completeTaskTranslateThunk, errorMessages.completingTask);
  },
});

export const selectAllAssignments = (state: RootState) => state.assignments.assignments;
export const selectProjectAssignments = (state: RootState, projectId: number) => state.assignments.assignments[projectId]?.assignments;
export const selectProjectAssignmentsTemp = (state: RootState, projectId: number, assignmentId: number) => state.assignments.assignments[projectId]?.assignmentsTemp?.[assignmentId];
export const selectAssignmentById = (state: RootState, projectId: number, assignmentId?: number) => !assignmentId
  ? null
  : state.assignments.assignments[projectId]?.assignments[assignmentId] || null;

export const selectChapterTasks = createSelector([selectAssignmentById], (assignment) => {
  if (!assignment) {
    return [];
  }

  const books = Object.values(assignment.books);
  const chapters = books.map(book => Object.values(book.chapters)).flat();
  const verses = chapters.map(chapter => Object.values(chapter.verses)).flat();

  return verses;
});

export const selectAllAssignmentTasksAsArray = createSelector([selectAssignmentById], (assignment) => {
  if (!assignment) {
    return [];
  }
  const books = Object.values(assignment.books);
  const chapters = books.map(book => Object.values(book.chapters)).flat();
  const verses = chapters.map(chapter => Object.values(chapter.verses)).flat();
  return verses.sort((a, b) => Number(b.isBookmarked) - Number(a.isBookmarked) || Number(a.status) - Number(b.status));
});

interface BooksCompletionState {
  [bookId: number]: {
    isCompleted: boolean;
    chapters: {
      [chapterId: number]: {
        isCompleted: boolean;
      };
    };
  };
}

export const selectBooksCompletionState = createSelector([selectAssignmentById], (assignment) => {
  if (!assignment) {
    return {};
  }

  const books: BooksCompletionState = Object.assign({}, ...Object.values(assignment.books).map((book) => {
    const chapters = Object.values(book.chapters);
    const tasks = chapters.map(chapter => Object.values(chapter.verses)).flat();
    const completedTasks = tasks.filter(({ status }) => status === TASK_STATUSES.COMPLETED).length;

    return {
      [book.id]: {
        isCompleted: tasks.length === completedTasks,
        chapters: Object.assign({}, ...chapters.map((chapter) => {
          const chapterTasks = Object.values(chapter.verses);
          const completedChapterTasks = chapterTasks.filter(({ status }) => status === TASK_STATUSES.COMPLETED).length; // TODO: Create util

          return (
            { [chapter.id]: { isCompleted: chapterTasks.length === completedChapterTasks } }
          );
        })),
      },
    };
  }));

  return books;
});

export const selectAssignmentTotalTasks = createSelector([selectAllAssignmentTasksAsArray], (tasks) => {
  return tasks.length;
});

export const selectAssignmentCompletedTasks = createSelector([selectAllAssignmentTasksAsArray], (tasks) => {
  return tasks.filter(({ status }) => status === TASK_STATUSES.COMPLETED).length;
});
export const selectReviewerCompletedTasks = createSelector([selectAllAssignmentTasksAsArray], (tasks) => {
  return tasks.filter(({ isReviewCompleted, editReviewerTranslation }) => isReviewCompleted && !editReviewerTranslation).length;
});

export const selectAssignmentsStatus = (state: RootState) => state.assignments?.status;
export const selectAssignmentsError = (state: RootState) => state.assignments?.error;

export const { markIncompleteTasksWithError, saveAssignmentTemp, updateTaskError, updateEditReviewTask } = assignmentsSlice.actions;

export default assignmentsSlice.reducer;
